import React, { useEffect, useRef } from 'react'
import styles from './styles.module.scss'

// Must match $page-grid-cell: the background grid is tiled from the top centre,
// each tile drawing its top and left edge
const GRID_CELL = 56
// Length of the orb's fading tail
const TAIL = 110
// Pause between orbs, and how long one takes to cross
const GAP_MS = [2500, 6000]
const TRAVEL_MS = [5000, 8000]
// Never more than this many on screen at once
const MAX_ORBS = 2

const between = ([min, max]: number[]) => min + Math.random() * (max - min)
const pick = (count: number) => Math.floor(Math.random() * count)

/**
 * Every few seconds, a small light glides along one line of the page's
 * background grid and fades out. Sits behind everything, faded the same way
 * as the grid, and is off entirely for reduced motion.
 */
const GridOrbs: React.FC = () => {
    const hostRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const host = hostRef.current
        if (!host || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
        let timer: ReturnType<typeof setTimeout>

        const spawn = () => {
            if (!document.hidden && host.childElementCount < MAX_ORBS) {
                // The grid is laid out on the page's width without the scrollbar
                // (innerWidth includes it, which would put orbs right of the lines)
                const width = document.documentElement.clientWidth
                const height = document.documentElement.clientHeight
                const orb = document.createElement('span')
                let keyframes: Keyframe[]

                if (Math.random() < 0.6) {
                    // Along a horizontal line in the top part of the page, either way
                    const rows = Math.max(1, Math.floor((height * 0.55) / GRID_CELL))
                    const y = GRID_CELL * (1 + pick(rows))
                    const forwards = Math.random() < 0.5
                    const [from, to] = forwards ? [-TAIL, width] : [width, -TAIL]
                    // Flipped when travelling left, so the tail trails behind
                    const flip = forwards ? '' : ' scaleX(-1)'
                    orb.className = `${styles.orb} ${styles.horizontal}`
                    orb.style.top = `${y}px`
                    keyframes = [
                        { transform: `translateX(${from}px)${flip}` },
                        { transform: `translateX(${to}px)${flip}` },
                    ]
                } else {
                    // Down a vertical line
                    const firstX = (width / 2 - GRID_CELL / 2) % GRID_CELL
                    const columns = Math.floor((width - firstX) / GRID_CELL) + 1
                    const x = firstX + GRID_CELL * pick(columns)
                    orb.className = `${styles.orb} ${styles.vertical}`
                    orb.style.left = `${x}px`
                    keyframes = [
                        { transform: `translateY(${-TAIL}px)` },
                        { transform: `translateY(${height * 0.8}px)` },
                    ]
                }

                host.appendChild(orb)
                const travel = orb.animate(keyframes, { duration: between(TRAVEL_MS), easing: 'ease-in-out' })
                // Fades in and out at either end of its run
                orb.animate([{ opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 1, offset: 0.8 }, { opacity: 0 }], {
                    duration: travel.effect?.getTiming().duration as number,
                })
                travel.onfinish = () => orb.remove()
            }
            timer = setTimeout(spawn, between(GAP_MS))
        }

        timer = setTimeout(spawn, 1200)
        return () => {
            clearTimeout(timer)
            host.replaceChildren()
        }
    }, [])

    return <div ref={hostRef} className={styles.host} aria-hidden="true" />
}

export default GridOrbs
